import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import "./Gallery.css";

import Lightbox, {
  ControllerRef,
  Slide,
  SlideshowRef,
  createModule,
  useLightboxState,
} from "yet-another-react-lightbox";
import Video from "yet-another-react-lightbox/plugins/video";
import "yet-another-react-lightbox/styles.css";

// import optional lightbox plugins
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

import {
  Checkbox,
  Loader,
  LoadingOverlay,
  Overlay,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import React from "react";
import {
  HiArrowLeft,
  HiArrowRight,
  HiDotsVertical,
  HiDownload,
  HiFolder,
  HiPause,
  HiPlay,
  HiShare,
  HiUsers,
  HiZoomIn,
  HiZoomOut,
} from "react-icons/hi";
import { HiMiniPaintBrush } from "react-icons/hi2";
import { LuGalleryThumbnails } from "react-icons/lu";
import {
  MdClose,
  MdCloseFullscreen,
  MdFolder,
  MdFolderShared,
  MdFullscreen,
} from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import useGlobalState, { globalSettings } from "../../globalState";
import { socket } from "../../services/socketService";
import { Settings } from "../../settings";
import { isTouchScreenDevice } from "../../utils/Utils";
import BottomDrawer from "../bottomSheet/BottomDrawer";
import LoaderMenu from "../menu/LoaderMenu";
import { FILE_OPTIONS_MENU_CONFIG, LOADER_MENU_CONFIG, MenuAction, MenuSection } from "../menu/menus";

import { FileDto, FolderDto } from "@braoshzmvavz/picnube-common";
import PhotoAlbum, { LayoutType } from "react-photo-album";
import { useAppSelector, useAppStore } from "../../app/hooks";
import { selectCurrentSpace } from "src/state/profile/profileSlice";
import {
  selectCurrentFiles,
  selectLoadingExplorer,
  selectLoadingFolder,
} from "src/state/explorer/explorerSlice";
import { useAutoHideYarlButtons } from "../../utils/hooks/useAutoHideYarlButtons";

export enum FILE_STATE {
  LOADED,
  ERROR,
  LOADING,
}

export default function Gallery({
  folders,
  files,
  loadMoreFn,
  onFolderClickFn,
  onActionClick,
  hiddenOptions,
  isMiniGallery,
  isSelectionEnabled = true,
  selectedFiles,
  setSelectedFiles,
  openLightboxAndPlayFromFile,
  setOpenLightboxAndPlayFromFile,
  demo = false,
  hardcodeSettings,
  enableInfiniteLoading = true,
}: {
  folders: FolderDto[];
  files: FileDto[];
  loadMoreFn: () => Promise<void> | void;
  onFolderClickFn: (folder: FolderDto) => void;
  onActionClick;
  hiddenOptions?: boolean;
  isMiniGallery?: boolean;
  isSelectionEnabled?: boolean;
  selectedFiles?: FileDto[];
  setSelectedFiles?: React.Dispatch<React.SetStateAction<FileDto[]>>;
  openLightboxAndPlayFromFile?: {
    position?: number;
    file?: any;
    remoteControl?: boolean;
  };
  setOpenLightboxAndPlayFromFile?: React.Dispatch<
    React.SetStateAction<{ position?: number; file?: any }>
  >;
  demo?: boolean;
  hardcodeSettings?: Settings;
  enableInfiniteLoading?: boolean;
}) {
  const isMenuOverlayEnabled = false;

  const theme = useMantineTheme();
  const [isOverlay, setIsOverlay] = useState(false);
  const [filesForPhotoAlbum, setFilesForPhotoAlbum] = useState([]);
  const [fileState, setFileState] = useState({});
  const [photoKey, setPhotoKey] = useState({});
  const [retryCount, setRetryCount] = useState({});
  const [lastViewedIndex, setLastViewedIndex] = useState(-1);
  const [settings, setSettings] = useGlobalState<Settings>(globalSettings);
  const computedColorScheme = useComputedColorScheme("dark");
  const navigate = useNavigate();
  const { hash } = useLocation();
  const loadingFolder = useAppSelector(selectLoadingFolder);
  const loadingExplorer = useAppSelector(selectLoadingExplorer);

  // Virtualization state: track which photos are visible
  const [visiblePhotoIds, setVisiblePhotoIds] = useState<Set<string>>(
    new Set()
  );
  const photoRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const isSettingIndexRef = useRef(false);

  const profileState = useAppSelector((state) => state.profile);
  const currentSpace = useAppSelector(selectCurrentSpace);
  const currentFiles = useAppSelector(selectCurrentFiles);

  const [index, setIndex] = useState(-1);
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);

  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [contextMenuState, setContextMenuState] = useState<{
    open: boolean;
    x: number;
    y: number;
    photo: any;
  }>({ open: false, x: 0, y: 0, photo: null });
  const [isRemoteControl, setIsRemoteControl] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [previousFilesLength, setPreviousFilesLength] = useState(0);

  const slideshowRef = React.useRef<SlideshowRef>(null);
  const controllerRef = React.useRef<ControllerRef>(null);
  const loadingRef = React.useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = React.useRef<boolean>(false);

  const THUMBNAIL_RERENDER_INTERVAL = 10000;
  const VIRTUALIZATION_ROOT_MARGIN = "200px"; // Load images 200px before they enter viewport

  useAutoHideYarlButtons({
    delay: 5000,
  });

  // Virtualization: Intersection Observer to track visible photos
  useEffect(() => {
    if (filesForPhotoAlbum.length === 0) {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: VIRTUALIZATION_ROOT_MARGIN,
      threshold: 0.01, // Trigger when at least 1% is visible
    };

    const observer = new IntersectionObserver((entries) => {
      setVisiblePhotoIds((prev) => {
        const next = new Set(prev);
        entries.forEach((entry) => {
          const photoId = entry.target.getAttribute("data-photo-id");
          if (photoId) {
            if (entry.isIntersecting) {
              next.add(photoId);
            } else {
              // Keep in cache for a bit to avoid flickering on fast scroll
              // Only remove if it's far from viewport
              const rect = entry.boundingClientRect;
              const viewportHeight = window.innerHeight;
              const isFarFromViewport =
                rect.bottom < -viewportHeight || rect.top > viewportHeight * 2;
              if (isFarFromViewport) {
                next.delete(photoId);
              }
            }
          }
        });
        return next;
      });
    }, observerOptions);

    observerRef.current = observer;

    // Observe all existing photo elements
    photoRefs.current.forEach((element) => {
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [filesForPhotoAlbum]);

  // Initialize visibility for photos near the top of the viewport
  useEffect(() => {
    if (filesForPhotoAlbum.length === 0) return;

    // Initially mark first batch as visible
    const initialVisible = new Set<string>();
    const initialCount = Math.min(50, filesForPhotoAlbum.length); // Load first 50
    filesForPhotoAlbum.slice(0, initialCount).forEach((photo) => {
      initialVisible.add(photo.id);
    });
    setVisiblePhotoIds(initialVisible);
  }, [filesForPhotoAlbum]);

  // Update hasMore state when files array changes
  useEffect(() => {
    setHasMore(true);
    if (files.length === previousFilesLength && files.length > 0) {
      setHasMore(false);
    }
    if (files.length == 0) {
      setHasMore(false);
    }
    setPreviousFilesLength(files.length);
  }, [files]);

  // Load more photos function (similar to Timeline)
  const loadMore = useCallback(async () => {
    // Use ref to check loading state to avoid dependency issues
    if (isLoadingMoreRef.current || !hasMore) return;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    // Use setTimeout to ensure React renders the loading state before async operation
    await new Promise((resolve) => setTimeout(resolve, 0));

    try {
      // Wrap loadMoreFn to ensure it returns a promise
      const result = loadMoreFn();
      if (result && typeof result.then === "function") {
        await result;
      } else {
        // If loadMoreFn doesn't return a promise, wrap it in a promise
        // This handles the case where loadMoreFn is synchronous or uses callbacks
        await new Promise<void>((resolve) => {
          // Give React time to render the loading state
          setTimeout(() => {
            // The actual loading happens in loadMoreFn, we just wait a bit
            // to ensure the loading indicator is visible
            setTimeout(resolve, 500);
          }, 50);
        });
      }
    } catch (error) {
      console.error("Error loading more photos:", error);
      setHasMore(false);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [hasMore, loadMoreFn]);

  // Infinite scroll implementation (similar to Timeline)
  useEffect(() => {
    if (!enableInfiniteLoading || !hasMore) return;

    const options = {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    } as IntersectionObserverInit;

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        loadMore();
      }
    }, options);

    const target = loadingRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
      observer.disconnect();
    };
  }, [hasMore, files, loadMore, enableInfiniteLoading]);

  // Load more plugin build // start
  function LoadMoreComponent({ children, ...props }) {
    const { slides, currentSlide, currentIndex } = useLightboxState();

    useEffect(() => {
      console.log("Slide: " + currentIndex + "/" + (slides?.length - 1));
      if (currentIndex == slides?.length - 1) {
        loadMoreFn();
      }
    }, [currentIndex, slides]);

    return <>{children}</>;
  }

  const loadMoreModule = createModule(
    "LoadMoreModule",
    LoadMoreComponent as any
  );

  function LoadMorePlugin({ addModule }) {
    addModule(loadMoreModule);
  }
  // Load more plugin build // end

  const isVideo = (file: FileDto) =>
    file.name.toLowerCase().endsWith(".mp4") ||
    file.name.toLowerCase().endsWith(".mov");

  useEffect(() => {
    if (!isRemoteControl) {
      //socket.disconnect();
      return;
    }

    socket.on("receive-command", (data) => {
      console.log("Received:", data.command);

      if (data.command === "PLAY") {
        play();
      }

      if (data.command === "PAUSE") {
        pause();
      }

      if (data.command === "NEXT") {
        next();
      }

      if (data.command === "PREVIOUS") {
        prev();
      }
    });
  }, [isRemoteControl]);

  useEffect(() => {
    const mappedPhotos = files.map((file: FileDto) => {
      if (isVideo(file)) {
        return {
          ...file,
          width: file.width ?? 1280,
          height: file.height ?? 720,
          src: file.thumbnailUrl,
        };
      }

      return {
        ...file,
        width: file.width ?? 1280,
        height: file.height ?? 720,
        src: file.thumbnailUrl,
      };
    });

    setFileState((oldFs) => {
      const newFs = {};
      mappedPhotos.forEach((mp) => {
        if (oldFs[mp.id] !== undefined && oldFs[mp.id] !== null) {
          newFs[mp.id] = oldFs[mp.id];
          return;
        }
        newFs[mp.id] = FILE_STATE.LOADING;
      });
      return newFs;
    });
    setPhotoKey(mappedPhotos.map((_) => Date.now()));

    setFilesForPhotoAlbum(mappedPhotos);
  }, [files]);

  useEffect(() => {
    if (!openLightboxAndPlayFromFile) return;
    if (
      !openLightboxAndPlayFromFile.file &&
      openLightboxAndPlayFromFile.position === undefined
    )
      return;

    let index = 0;

    if (openLightboxAndPlayFromFile.file) {
      index = files.findIndex(
        (f) => f.id === openLightboxAndPlayFromFile.file.id
      );
    }

    if (openLightboxAndPlayFromFile.position !== undefined) {
      index = openLightboxAndPlayFromFile.position;
    }

    setIndex(index);

    if (openLightboxAndPlayFromFile.remoteControl) {
      // Pause slideshow
      setTimeout(() => {
        if (slideshowRef.current?.playing) {
          slideshowRef.current.pause();
        }
      }, 1000);

      setIsRemoteControl(true);
    } else {
      // Play slideshow
      setTimeout(() => {
        if (!slideshowRef.current?.playing) {
          slideshowRef.current.play();
        }
      }, 1000);
    }
  }, [openLightboxAndPlayFromFile]);

  const filesForLightbox: Slide[] = useMemo(() => {
    return files.map((file: FileDto) => {
      if (isVideo(file)) {
        return {
          type: "video",
          title: file.name,
          width: file.width ?? 1280,
          height: file.height ?? 720,
          poster: file.thumbnailUrl,
          sources: [
            {
              src: file.url,
              type: "video/mp4",
            },
          ],
        };
      }

      return {
        ...file,
        type: "image",
        src: file.url,
      };
    });
  }, [files]);

  function next() {
    controllerRef.current?.next();
  }

  function prev() {
    controllerRef.current?.prev();
  }

  function play() {
    slideshowRef.current?.play();
  }

  function pause() {
    slideshowRef.current?.pause();
  }

  function onImgLoad(e: any, photo: any) {
    setFileState((fs) => ({ ...fs, [photo.id]: FILE_STATE.LOADED }));
  }

  function onImgError(e: any, photo: any) {
    // TODO: add flag to toggle retries
    if (true) {
      setFileState((fs) => ({ ...fs, [photo.id]: FILE_STATE.ERROR }));
      return;
    }

    const currentRetries = retryCount[photo.id] || 0;

    if (currentRetries < 5) {
      // Increment retry count
      setRetryCount((prev) => ({ ...prev, [photo.id]: currentRetries + 1 }));

      // Trigger photo key update to force re-render
      setTimeout(() => {
        setPhotoKey((pk) => ({ ...pk, [photo.id]: Date.now() }));
      }, THUMBNAIL_RERENDER_INTERVAL);
    } else {
      // Max retries reached, mark as error
      setFileState((fs) => ({ ...fs, [photo.id]: FILE_STATE.ERROR }));
    }
  }

  function getPhotoClassName(photo: any) {
    let className =
      "photo overflow-hidden relative cursor-pointer transition-all box-border";

    if (settings.photoAlbum_roundedBorder) {
      className += " rounded-xl";
    }

    if (filesForPhotoAlbum[lastViewedIndex]?.id === photo.id) {
      className += " border-4 border-blue-500";
    }

    if (
      isSelectionEnabled &&
      (selectedFiles?.includes(photo.id) || selectedFiles === null)
    ) {
      className += " border-2 border-blue-500";
    }

    if (fileState[photo.id] === FILE_STATE.ERROR) {
      className += " photo--error";
    } else if (fileState[photo.id] === FILE_STATE.LOADED) {
      className += " photo--loaded";
    } else {
      className += " photo--loading";
    }

    return className;
  }

  function getPhotoErrorClassName(photo: any) {
    let className =
      "photo-error w-full h-full dark:bg-dark-700 bg-grey-200 flex flex-col items-center justify-center gap-2 text-grey-600 hover:bg-dark-800";

    return fileState[photo.id] === FILE_STATE.ERROR
      ? className + " visible"
      : "invisible h-0 w-0";
  }

  function getPhotoLoadingClassName(photo: any) {
    let className =
      "photo-loading w-full h-full dark:bg-dark-700 bg-grey-200 animate-pulse";

    return fileState[photo.id] !== FILE_STATE.ERROR &&
      fileState[photo.id] !== FILE_STATE.LOADED
      ? className + " visible"
      : "invisible h-0 w-0";
  }

  function onPhotoClick(photo: any) {
    if (
      isSelectionEnabled &&
      ((!!selectedFiles && selectedFiles.length > 0) || selectedFiles === null)
    ) {
      if (selectedFiles === null) {
        return;
      }
      if (selectedFiles.includes(photo.id)) {
        setSelectedFiles(selectedFiles.filter((f) => f !== photo.id));
      } else {
        setSelectedFiles([...selectedFiles, photo.id]);
      }
      return;
    }

    const photoIndex = filesForPhotoAlbum.findIndex((p) => p.id === photo.id);
    isSettingIndexRef.current = true;
    setIndex(photoIndex);
    // Reset flag after state update
    setTimeout(() => {
      isSettingIndexRef.current = false;
    }, 0);
  }

  function onPhotoContextMenu(e: React.MouseEvent, photo: any) {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuState({ open: true, x: e.clientX, y: e.clientY, photo });
  }

  function closeContextMenu() {
    setContextMenuState((prev) => ({ ...prev, open: false }));
  }

  function renderContextMenu() {
    if (!contextMenuState.open || !contextMenuState.photo) return null;

    // Mobile doesn't have right-click context menus - use long press or the existing three-dot menu
    if (isMobile) {
      return null;
    }

    // Use the same rendering logic as LoaderMenu but position at cursor
    // Note: profileState is already available from the component level

    const showMenuAction = (menuAction: MenuAction) => {
      if (!!menuAction.showIf) {
        return menuAction.showIf(
          { file: { ...contextMenuState.photo }, demo },
          profileState.profile?.email
        );
      }
      return true;
    };

    const showMenuSection = (menuSection: MenuSection) => {
      return menuSection.actions.some(showMenuAction);
    };

    const ctx = { file: { ...contextMenuState.photo }, demo };

    return (
      <>
        <div
          className="fixed inset-0 z-40"
          onClick={() => closeContextMenu()}
          onContextMenu={(e) => {
            e.preventDefault();
            closeContextMenu();
          }}
        />
        <div
          className="fixed z-50 shadow-md"
          style={{
            left: contextMenuState.x,
            top: contextMenuState.y,
          }}
        >
          <div className="bg-white dark:bg-grey-900 rounded-lg border border-grey-300 dark:border-grey-700 py-1 min-w-[200px]">
            {FILE_OPTIONS_MENU_CONFIG.filter(showMenuSection).map(
              (menuSection, index1) => {
                return (
                  <div key={index1 + "menuSection"}>
                    {index1 === 0 ? (
                      ""
                    ) : (
                      <div className="border-t border-grey-300 dark:border-grey-700 my-1" />
                    )}

                    <div className="px-3 py-1 text-xs font-medium text-grey-500">
                      {menuSection.name}
                    </div>

                    {menuSection.actions
                      .filter(showMenuAction)
                      .map((menuAction, index2) => {
                        return (
                          <div
                            key={index1 + index2 + "menuAction"}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-grey-100 dark:hover:bg-dark-800"
                            onClick={() => {
                              closeContextMenu();
                              onActionClick(menuAction, ctx);
                            }}
                          >
                            <menuAction.icon className="flex w-4 h-4" />
                            <div className="flex flex-col">
                              <span>{menuAction.name}</span>
                              {menuAction?.description ? (
                                <div className="text-xs text-grey-500 mt-0.5">
                                  {menuAction.description}
                                </div>
                              ) : (
                                ""
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
              }
            )}
          </div>
        </div>
      </>
    );
  }

  function getGalleryItemMaxWidth() {
    // TODO: IMPROVE
    //if (isMobile) return "100%";
    //if (filesForPhotoAlbum.length < 10) return "15rem";
    return "100%";
  }

  function getPhotoKey(photo: any) {
    return photoKey[photo.id];
  }

  function renderFileOptions(photo: any) {
    if (hiddenOptions) return null;

    let content;

    if (isMobile) {
      content = (
        <BottomDrawer
          ctx={{
            file: { ...photo },
            demo,
            space: currentSpace,
          }}
          menuConfig={FILE_OPTIONS_MENU_CONFIG}
          onActionClick={onActionClick}
          targetElement={
            <div
              className={`${
                isTouchScreenDevice()
                  ? "photo-options--mobile"
                  : "photo-options"
              } flex justify-center items-center p-2 hover:bg-dark-900 active:bg-dark-999 cursor-pointer rounded-xl`}
            >
              <HiDotsVertical className="flex w-4 h-4"></HiDotsVertical>
            </div>
          }
        ></BottomDrawer>
      );
    } else {
      content = (
        <LoaderMenu
          ctx={{
            file: { ...photo },
            demo,
          }}
          menuConfig={FILE_OPTIONS_MENU_CONFIG}
          onActionClick={onActionClick}
          targetElement={
            <div
              className={`${
                isTouchScreenDevice()
                  ? "photo-options--mobile"
                  : "photo-options"
              } flex justify-center items-center p-2 hover:bg-dark-900 active:bg-dark-999 cursor-pointer rounded-xl`}
            >
              <HiDotsVertical className="flex w-4 h-4"></HiDotsVertical>
            </div>
          }
          onOpened={() => setIsOverlay(true)}
          onClosed={() => setIsOverlay(false)}
        ></LoaderMenu>
      );
    }

    return (
      <div
        className="absolute top-1 right-1 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </div>
    );
  }

  function renderVideoIconOverlay(photo: any) {
    if (isVideo(photo)) {
      return (
        <HiPlay className="photo-album-media-container__video-indicator absolute text-5xl opacity-60 hover:opacity-80"></HiPlay>
      );
    }
  }

  function renderFileSelectionCheckbox(photo: any) {
    if (hiddenOptions) return null;
    if (!isSelectionEnabled) return null;

    if (!!selectedFiles && selectedFiles.includes(photo.id)) {
      return (
        <div
          className="absolute top-2 left-2 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            className={`$text-blue-400 cursor-pointer`}
            checked={selectedFiles.includes(photo.id)}
            onClick={() => {
              setSelectedFiles(selectedFiles.filter((f) => f !== photo.id));
            }}
          />
        </div>
      );
    }

    return (
      <div
        className="absolute top-2 left-2 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          className={`${
            isTouchScreenDevice() ? "photo-options--mobile" : "photo-options"
          } text-blue-400 cursor-pointer opacity-25`}
          checked={selectedFiles === null}
          onClick={() => {
            if (!!selectedFiles) {
              setSelectedFiles([...selectedFiles, photo.id]);
            }
          }}
        />
      </div>
    );
  }

  function mapPhotoLayoutClassName(layout: LayoutType, className: string) {
    if (layout === "rows") {
      return `${className}--row `;
    }

    if (layout === "columns") {
      return `${className}--col`;
    }

    if (layout === "masonry") {
      return `${className}--mas`;
    }
  }

  function parseImageSrc(src: string) {
    if (src.startsWith("http")) {
      return src;
    }
    return `${window.location.origin}/${src}`;
  }

  function renderPhotoAlbum() {
    return (
      <PhotoAlbum
        photos={filesForPhotoAlbum as any}
        layout={
          hardcodeSettings?.photoAlbum_layout ?? settings.photoAlbum_layout
        }
        spacing={
          hardcodeSettings?.photoAlbum_spacing ?? settings.photoAlbum_spacing
        }
        targetRowHeight={
          !!isMiniGallery
            ? settings.photoAlbum_targetRowHeight * 1
            : settings.photoAlbum_targetRowHeight
        }
        renderPhoto={({
          photo,
          wrapperStyle,
          imageProps: { src, alt, style, ...restImageProps },
        }) => {
          const photoWithId = photo as any as FileDto;
          const photoId = photoWithId.id;
          const isVisible = visiblePhotoIds.has(photoId);
          const photoRef = (element: HTMLDivElement | null) => {
            if (element) {
              photoRefs.current.set(photoId, element);
              // Observe the element immediately when it's mounted
              if (observerRef.current) {
                observerRef.current.observe(element);
              }
            } else {
              // Unobserve when element is unmounted
              const existingElement = photoRefs.current.get(photoId);
              if (existingElement && observerRef.current) {
                observerRef.current.unobserve(existingElement);
              }
              photoRefs.current.delete(photoId);
            }
          };

          return (
            <div
              ref={photoRef}
              data-photo-id={photoId}
              className={getPhotoClassName(photo)}
              style={{ ...wrapperStyle, maxWidth: getGalleryItemMaxWidth() }}
              onClick={() => {
                closeContextMenu();
                onPhotoClick(photo);
              }}
              onContextMenu={(e) => onPhotoContextMenu(e, photo)}
            >
              <div className={getPhotoErrorClassName(photo)}>
                <HiMiniPaintBrush className="size-8" />
                <Text size="xs" className="text-center">
                  Generating thumbnail...
                </Text>
              </div>

              <div className={getPhotoLoadingClassName(photo)}></div>

              <div
                className={`photo-album-media-container flex justify-center items-center ${
                  settings.gallery_thumbnailHoverEffect
                    ? "photo-album-media-container--hover-effect"
                    : ""
                }`}
              >
                {isVisible ? (
                  <>
                    <img
                      key={getPhotoKey(photo)}
                      src={parseImageSrc(src)}
                      alt={alt}
                      style={style}
                      onLoad={(e) => onImgLoad(e, photo)}
                      onError={(e) => onImgError(e, photo)}
                      {...restImageProps}
                    />
                    {renderVideoIconOverlay(photo)}
                  </>
                ) : (
                  // Placeholder to maintain layout and scroll position
                  <div
                    style={{
                      ...style,
                      backgroundColor:
                        computedColorScheme === "dark" ? "#1a1a1a" : "#e5e5e5",
                    }}
                    className="photo-placeholder"
                  />
                )}
              </div>

              {renderFileOptions(photo)}
              {renderFileSelectionCheckbox(photo)}
            </div>
          );
        }}
      />
    );
  }

  function getNavigationButtonClassName() {
    if (isTouchScreenDevice())
      return "opacity-50 nav-button nav-button--mobile";
    return "opacity-50 hover:opacity-100 nav-button";
  }

  // TODO: make configurable through settings
  function renderBottomControls() {
    return null;
    return (
      <div
        className="fixed bottom-2 right-auto left-auto
      flex flex-row gap-4 justify-center items-center
      border-2 border-grey-700
      w-fit h-10 p-2 rounded-lg
      bg-grey-900 bg-opacity-50
      opacity-50 hover:opacity-100
      "
      >
        <HiArrowLeft className="text-3xl opacity-50 hover:opacity-100 cursor-pointer"></HiArrowLeft>
        <HiArrowRight className="text-3xl opacity-50 hover:opacity-100 cursor-pointer"></HiArrowRight>
      </div>
    );
  }

  const onLightboxClose = () => {
    if (!!setOpenLightboxAndPlayFromFile) {
      setOpenLightboxAndPlayFromFile(null);

      // Pause slideshow
      if (slideshowRef.current?.playing) {
        slideshowRef.current?.pause();
      }
    }

    setIndex(-1);
  };

  function renderLightbox() {
    const toolbarButtons = [
      <button key="download-button" type="button" className="yarl__button">
        <HiDownload size={18}></HiDownload>
      </button>,
      <button key="share-button" type="button" className="yarl__button">
        <HiShare size={18}></HiShare>
      </button>,
      "close",
    ];

    return (
      <Lightbox
        slides={filesForLightbox}
        open={index >= 0}
        index={index}
        on={{
          entered: () => {
            setIsLightboxOpen(true);
          },
          exited: () => {
            setIsLightboxOpen(false);
          },
          view: ({ index: currentIndex }) => {
            // Only sync index if the change came from user navigation (not from our programmatic set)
            if (
              !isSettingIndexRef.current &&
              currentIndex !== index &&
              currentIndex >= 0
            ) {
              setIndex(currentIndex);
            }
          },
        }}
        toolbar={{
          buttons: toolbarButtons,
        }}
        close={() => onLightboxClose()}
        carousel={{
          finite: true,
          preload: 1,
        }}
        plugins={[
          Fullscreen,
          Slideshow,
          //Thumbnails, // TODO: loads full images?
          Zoom,
          //LoadMorePlugin, // TODO: fix infinite loop error when only 1 image gallery
          Video,
          //Share,
        ]}
        slideshow={{ ref: slideshowRef, delay: settings.slideshow_delay }}
        /*thumbnails={{
          padding: 0,
          border: 0,
          imageFit: "cover",
          showToggle: true,
        }}*/
        controller={{
          ref: controllerRef,
          closeOnBackdropClick: false, // TODO: make configurable
          closeOnPullDown: true,
          closeOnPullUp: true,
        }}
        render={{
          controls: renderBottomControls,
          iconClose: () => <MdClose size={24}></MdClose>,
          //iconShare: () => <HiShare size={24}></HiShare>,
          iconEnterFullscreen: () => <MdFullscreen size={24}></MdFullscreen>,
          iconExitFullscreen: () => (
            <MdCloseFullscreen size={24}></MdCloseFullscreen>
          ),
          iconZoomIn: () => <HiZoomIn size={24}></HiZoomIn>,
          iconZoomOut: () => <HiZoomOut size={24}></HiZoomOut>,
          iconSlideshowPlay: () => <HiPlay size={24}></HiPlay>,
          iconSlideshowPause: () => <HiPause size={24}></HiPause>,
          /*iconThumbnailsVisible: () => (
            <LuGalleryThumbnails size={24}></LuGalleryThumbnails>
          ),
          iconThumbnailsHidden: () => (
            <LuGalleryThumbnails size={24}></LuGalleryThumbnails>
          ),*/
          iconPrev: () => (
            <HiArrowLeft
              className={getNavigationButtonClassName()}
              size={20}
            ></HiArrowLeft>
          ),
          iconNext: () => (
            <HiArrowRight
              className={getNavigationButtonClassName()}
              size={20}
            ></HiArrowRight>
          ),
        }}
      />
    );
  }

  // TODO: deprecate
  function isSharedFolder(folder: FolderDto) {
    return false;
    //return folder.owner != profileState.profile.email;
  }

  function renderFolderIcon(folder: FolderDto) {
    if (isMobile) {
      return (
        <div className="relative">
          {isSharedFolder(folder) ? (
            <MdFolderShared className="text-2xl" />
          ) : (
            <MdFolder className="text-2xl text-blue-400" />
          )}
        </div>
      );
    }

    if (isSharedFolder(folder)) {
      return (
        <div className="relative">
          <HiFolder className="xs:text-7xl text-2xl" />
          <HiUsers
            className="absolute text-xs text-dark-600 bottom-1 right-2
            xs:text-dark-900 xs:bottom-5 xs:right-4 xs:text-lg"
          />
        </div>
      );
    }

    return <HiFolder className="xs:text-7xl text-2xl text-blue-400" />;
  }

  const getFolderClassName = (folder: FolderDto) => {
    const isDarkMode = computedColorScheme === "dark";
    let colorOptions: string;
    let borderOptions: string = "";

    if (isDarkMode) {
      colorOptions = isMobile
        ? "hover:bg-grey-900 text-grey-300"
        : "border-b border-grey-800 hover:bg-grey-900 text-grey-300";
    } else {
      colorOptions = isMobile
        ? "hover:bg-grey-100 text-grey-900"
        : "border-b border-grey-200 hover:bg-grey-100 text-grey-900";
    }

    if (!isMobile) {
      borderOptions = "rounded-lg";
    }

    return `folder ${colorOptions} ${borderOptions}
      flex justify-start items-center gap-2 py-2 px-4 cursor-pointer 
      xs:border-none xs:w-52 xs:flex-col xs:gap-0 xs:text-center
      relative ${isMobile ? "rounded-lg" : ""}`;
  };

  const getFolderOptionsClassName = (folder: FolderDto) => {
    const isDarkMode = computedColorScheme === "dark";
    let colorOptions: string;

    if (isDarkMode) {
      colorOptions = "hover:bg-dark-900 hover:bg-dark-600 active:bg-dark-999";
    } else {
      colorOptions = "hover:bg-grey-300 active:bg-grey-400";
    }

    const folderOptions = isTouchScreenDevice()
      ? "folder-options--mobile"
      : "folder-options";

    return `${folderOptions} ${colorOptions} 
      flex justify-center items-center p-2 cursor-pointer rounded-xl`;
  };

  const renderFolderOptions = (folder: FolderDto) => {
    if (hiddenOptions) return null;

    let content;

    if (isMobile) {
      content = (
        <BottomDrawer
          ctx={{ folder, demo, space: currentSpace, folderFiles: currentFiles }}
          menuConfig={LOADER_MENU_CONFIG}
          onActionClick={onActionClick}
          targetElement={
            <div className={getFolderOptionsClassName(folder)}>
              <HiDotsVertical className="flex w-4 h-4"></HiDotsVertical>
            </div>
          }
        ></BottomDrawer>
      );
    } else {
      content = (
        <LoaderMenu
          ctx={{ folder, demo, space: currentSpace, folderFiles: currentFiles }}
          menuConfig={LOADER_MENU_CONFIG}
          onActionClick={onActionClick}
          targetElement={
            <div className={getFolderOptionsClassName(folder)}>
              <HiDotsVertical className="flex w-4 h-4"></HiDotsVertical>
            </div>
          }
          onOpened={() => setIsOverlay(true)}
          onClosed={() => setIsOverlay(false)}
        ></LoaderMenu>
      );
    }

    return (
      <div
        className="absolute top-1 right-1 pr-1"
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </div>
    );
  };

  const renderFolders = () => {
    if (folders?.length === 0) return <></>;

    return (
      <div className={`flex flex-col xs:flex-row xs:flex-wrap mb-4`}>
        {[...folders] // sort mutates the array
          .sort((a, b) => a.name.localeCompare(b.name, "en", { numeric: true }))
          .map((folder) => {
            return (
              <>
                <div
                  key={folder.id}
                  onClick={() => onFolderClickFn(folder as any)}
                  className={getFolderClassName(folder)}
                >
                  {renderFolderIcon(folder)}

                  {folder.name}

                  {renderFolderOptions(folder)}
                </div>
                {isMobile ? (
                  <div className="w-full border-b dark:border-grey-800 border-grey-200"></div>
                ) : null}
              </>
            );
          })}
      </div>
    );
  };

  const renderPhotoAlbumOrLoading = () => {
    if (loadingFolder || loadingExplorer) {
      return (
        <LoadingOverlay
          visible={true}
          zIndex={400}
          overlayProps={{
            blur: "xl",
            color: computedColorScheme === "dark" ? "black" : "black",
            opacity: computedColorScheme === "dark" ? "70%" : "30%",
          }}
          loaderProps={{ color: "blue", type: "oval", size: "md" }}
        ></LoadingOverlay>
      );
    }
    return (
      <div className="relative">
        {renderPhotoAlbum()}

        {/* Infinite scroll trigger element and loading indicator */}
        {enableInfiniteLoading && hasMore && (
          <div
            ref={loadingRef}
            className="flex justify-center items-center py-8 min-h-[80px]"
          >
            {isLoadingMore ? (
              <div className="flex flex-col items-center gap-2">
                <Loader color="blue" size="md"></Loader>
                <Text size="sm" c="dimmed">
                  Loading more photos...
                </Text>
              </div>
            ) : (
              <div className="h-20 pointer-events-none"></div>
            )}
          </div>
        )}

        {/* End of content indicator */}
        {/*enableInfiniteLoading && !hasMore && files.length > 0 && (
          <div className="flex justify-center items-center py-8">
            <Text size="sm" c="dimmed">
              You've reached the end of the gallery
            </Text>
          </div>
        )*/}
      </div>
    );
  };

  return (
    <div className={`${isMobile ? "p-[0.25rem]" : ""}`}>
      {isMenuOverlayEnabled && isOverlay && (
        <Overlay
          color={computedColorScheme === "dark" ? "#000" : "#aaa"}
          backgroundOpacity={0.45}
          blur={1}
        />
      )}
      {renderFolders()}

      {renderPhotoAlbumOrLoading()}

      {renderLightbox()}

      {renderContextMenu()}
    </div>
  );
}
